import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("stancor_user") || "null"); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem("stancor_token"));
  // Instant boot: if we have a cached user + token, render immediately and re-validate in background
  const [loading, setLoading] = useState(() => !(localStorage.getItem("stancor_token") && localStorage.getItem("stancor_user")));
  const [expiresAt, setExpiresAt] = useState(() => localStorage.getItem("stancor_expires"));
  const [showTimeout, setShowTimeout] = useState(false);
  const _cachedPerms = (() => {
    try { return JSON.parse(localStorage.getItem("stancor_perms") || "null"); } catch { return null; }
  })();
  const [role, setRole] = useState(_cachedPerms?.role ?? null);
  const [permsLoaded, setPermsLoaded] = useState(!!_cachedPerms);
  const [allowedPages, setAllowedPages] = useState(_cachedPerms?.allowed_pages ?? null);
  const [home, setHome] = useState(_cachedPerms?.home ?? null);
  const [visibility, setVisibility] = useState(_cachedPerms?.visibility || { see_prices: true, see_customers: true });
  // Role-preview mode: admin can "impersonate" another role's page visibility
  // without changing anything server-side. Purely a client-side UX helper.
  const [preview, setPreview] = useState(() => {
    try {
      const raw = localStorage.getItem("stancor_preview");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  // Set axios header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Session timeout warning — 5 min before expiry
  useEffect(() => {
    if (!expiresAt) return;
    const MAX_DELAY = 2147483647; // setTimeout overflows above ~24.8 days and fires immediately
    const exp = new Date(expiresAt).getTime();
    const now = Date.now();
    const msUntilExpiry = exp - now;
    const msUntilWarning = msUntilExpiry - 5 * 60 * 1000; // 5 min before

    if (warningRef.current) clearTimeout(warningRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (msUntilExpiry <= 0) return; // already expired — the /auth/me 401 handler signs out

    if (msUntilWarning <= 0) {
      setShowTimeout(true);
    } else if (msUntilWarning <= MAX_DELAY) {
      warningRef.current = setTimeout(() => setShowTimeout(true), msUntilWarning);
    }

    if (msUntilExpiry <= MAX_DELAY) {
      timeoutRef.current = setTimeout(() => {
        localStorage.removeItem("stancor_token");
        localStorage.removeItem("stancor_expires");
        setToken(null);
        setUser(null);
        setShowTimeout(false);
      }, msUntilExpiry);
    }

    return () => {
      if (warningRef.current) clearTimeout(warningRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [expiresAt]);

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      // No token → cached user is stale; clear it so the login page shows
      setUser(null);
      localStorage.removeItem("stancor_user");
      localStorage.removeItem("stancor_perms");
      sessionStorage.removeItem("stancor_landed");
      setLoading(false);
      setHome(null);
      setPermsLoaded(false);
      return;
    }
    axios.get(`${BACKEND_URL}/api/auth/me`)
      .then((res) => {
        setUser(res.data.user);
        localStorage.setItem("stancor_user", JSON.stringify(res.data.user));
        if (res.data.expires_at) {
          setExpiresAt(res.data.expires_at);
          localStorage.setItem("stancor_expires", res.data.expires_at);
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("stancor_token");
          localStorage.removeItem("stancor_expires");
          localStorage.removeItem("stancor_user");
          localStorage.removeItem("stancor_perms");
          setToken(null);
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
    // Fetch role/allowed_pages in parallel — non-blocking
    axios.get(`${BACKEND_URL}/api/auth/permissions`)
      .then((res) => {
        setRole(res.data.role);
        setAllowedPages(res.data.allowed_pages || []);
        setHome(res.data.home || "/");
        setVisibility(res.data.visibility || { see_prices: true, see_customers: true });
        localStorage.setItem("stancor_perms", JSON.stringify({
          role: res.data.role, allowed_pages: res.data.allowed_pages || [], home: res.data.home || "/",
          visibility: res.data.visibility || { see_prices: true, see_customers: true }
        }));
      })
      .catch(() => {})
      .finally(() => setPermsLoaded(true));
  }, [token]);

  const login = useCallback(async (username, password, rememberMe = false) => {
    const res = await axios.post(`${BACKEND_URL}/api/auth/login`, { username, password, remember_me: rememberMe });
    const { token: t, user: u, expires_at } = res.data;
    localStorage.setItem("stancor_token", t);
    localStorage.setItem("stancor_expires", expires_at);
    localStorage.setItem("stancor_user", JSON.stringify(u));
    setToken(t);
    setUser(u);
    setExpiresAt(expires_at);
    setShowTimeout(false);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`); } catch { /* ignore logout errors */ }
    localStorage.removeItem("stancor_token");
    localStorage.removeItem("stancor_expires");
    localStorage.removeItem("stancor_preview");
    localStorage.removeItem("stancor_user");
    localStorage.removeItem("stancor_perms");
    setToken(null);
    setUser(null);
    setExpiresAt(null);
    setShowTimeout(false);
    setRole(null);
    setAllowedPages(null);
    setPreview(null);
  }, []);

  const dismissTimeout = useCallback(() => setShowTimeout(false), []);

  // Role preview: admin picks a role → we mock allowedPages client-side.
  // `pages` should be the exact list from GET /api/settings/roles.permissions[role].
  const startPreview = useCallback((role, pages, username = null) => {
    const p = { role, pages: pages || [], username };
    localStorage.setItem("stancor_preview", JSON.stringify(p));
    setPreview(p);
  }, []);
  const exitPreview = useCallback(() => {
    localStorage.removeItem("stancor_preview");
    setPreview(null);
  }, []);

  const timeRemaining = expiresAt ? Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 60000)) : null;

  // Helper: is a given nav path allowed for the current user?
  // Preview mode wins when active (except for admin preview which sees all).
  // Otherwise: admin always sees everything. If allowedPages is null (still
  // loading / permission fetch failed), fall back to allowing everything so
  // the app is never accidentally locked out.
  const pageAllowed = useCallback((path) => {
    if (!user) return false;
    if (preview) {
      if (preview.role === "admin") return true;
      return (preview.pages || []).includes(path);
    }
    if (user.is_admin || role === "admin") return true;
    if (!allowedPages) return true;
    return allowedPages.includes(path);
  }, [user, role, allowedPages, preview]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout,
      isAuthenticated: !!user,
      showTimeout, dismissTimeout, timeRemaining,
      role, allowedPages, pageAllowed, visibility, permsLoaded, home,
      preview, startPreview, exitPreview,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
